/**
 * ImportStatusBadge - Displays document import status from documentImportStatusEnum.
 * Maps status values to label + color (CSS vars) + icon.
 */

import {
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRightLeft,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

type ImportStatus =
  | "detected"
  | "parsing_failed"
  | "partial"
  | "complete"
  | "superseded";

const STATUS_CONFIG: Record<
  ImportStatus,
  { label: string; color: string; icon: typeof CheckCircle2 }
> = {
  detected: {
    label: "Detected",
    color: "bg-muted text-muted-foreground",
    icon: Clock,
  },
  parsing_failed: {
    label: "Parse Failed",
    color: "bg-destructive/10 text-destructive",
    icon: AlertCircle,
  },
  partial: {
    label: "Partial",
    color: "bg-warning/10 text-warning",
    icon: Loader2,
  },
  complete: {
    label: "Complete",
    color: "bg-success/10 text-success",
    icon: CheckCircle2,
  },
  superseded: {
    label: "Superseded",
    color: "bg-muted text-muted-foreground line-through",
    icon: ArrowRightLeft,
  },
};

interface ImportStatusBadgeProps {
  status: ImportStatus | string | null | undefined;
  className?: string;
}

export function ImportStatusBadge({
  status,
  className,
}: ImportStatusBadgeProps) {
  const config = STATUS_CONFIG[(status as ImportStatus) || "detected"];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Badge
      variant="secondary"
      className={cn("gap-1 text-xs font-normal", config.color, className)}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}
